import { createInstance } from '@amplitude/analytics-node';
import { Experiment, type RemoteEvaluationClient, type RemoteEvaluationConfig, type Variant } from '@amplitude/experiment-node-server';
import { ampli } from '@magicschool/ampli-server';
import { logger } from '@magicschool/logger';
import { AMPLITUDE_API_KEY, AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY, CONTROL, OFF, ON } from './constants';
import { LOCAL_OVERRIDES } from './overrides';
import type { AmplitudeExperimentKey, AmplitudeFeatureFlagKey, SharedAnalyticsUserPropertyArgs } from './types';
import { buildAmplitudeUserProperties } from './util';

/**
 * Every page render awaits this fetch, and the SDK defaults (10 s, 8 retries) held pages 30 to 40 s during
 * Amplitude outages. Normal p99 is about 0.2 s; a fetch that gives up reads every flag as its default.
 */
const FLAG_FETCH_TIMEOUT_MS = 1_000;
const FLAG_FETCH_RETRIES = 1;
const FLAG_FETCH_RETRY_BACKOFF_MS = 100;

const FLAG_FETCH_CONFIG = {
  fetchTimeoutMillis: FLAG_FETCH_TIMEOUT_MS,
  fetchRetries: FLAG_FETCH_RETRIES,
  fetchRetryBackoffMinMillis: FLAG_FETCH_RETRY_BACKOFF_MS,
  fetchRetryTimeoutMillis: FLAG_FETCH_TIMEOUT_MS,
} satisfies RemoteEvaluationConfig;

let experimentServer: RemoteEvaluationClient;
let variants: Partial<Record<AmplitudeExperimentKey | AmplitudeFeatureFlagKey, Variant>> | undefined;

let amplitudeInitialized = false;

export const handleAmplitudeExperimentReset = () => {
  variants = undefined;
  amplitudeInitialized = false;
};

// ------ Amplitude Analytics ------
const initServerSideAmplitudeAnalytics = () => {
  variants = undefined;
  // by default we don't run on our local environments
  if (process.env.NEXT_PUBLIC_USE_AMPLITUDE !== 'true' || !AMPLITUDE_API_KEY) return;

  const instance = createInstance();
  instance.init(AMPLITUDE_API_KEY);
  ampli.load({ client: { instance } });
  amplitudeInitialized = true;
};

/**
 * Call before any typed `ampli.<event>(...)` call on the `@magicschool/ampli-server` singleton —
 * unlike `analyticsTrackServerWithUserId`, nothing loads it for you. A no-op once already loaded,
 * and a no-op entirely when analytics are disabled (matches the other functions in this file).
 */
export const ensureServerAmpliLoaded = () => {
  if (!amplitudeInitialized) {
    initServerSideAmplitudeAnalytics();
  }
};

type AnalyticsTrackServerWithUserIdParams = {
  eventType: string;
  eventProperties: Record<string, unknown>;
  userId: string;
  deviceId?: string;
  sessionId?: number;
};
export const analyticsTrackServerWithUserId = ({
  eventType,
  eventProperties,
  userId,
  deviceId,
  sessionId,
}: AnalyticsTrackServerWithUserIdParams) => {
  // by default we don't run on our local environments
  if (process.env.NEXT_PUBLIC_USE_AMPLITUDE !== 'true' || !AMPLITUDE_API_KEY) return;

  if (!amplitudeInitialized) {
    initServerSideAmplitudeAnalytics();
  }

  ampli.client.track(eventType, eventProperties, {
    user_id: userId,
    device_id: deviceId || undefined,
    session_id: sessionId || undefined,
  });
};

export const analyticsTrackServerAnonymous = (
  eventType: string,
  eventProperties: Record<string, unknown>,
  deviceId: string,
  sessionId?: number,
) => {
  // by default we don't run on our local environments
  if (process.env.NEXT_PUBLIC_USE_AMPLITUDE !== 'true' || !AMPLITUDE_API_KEY) return;

  if (!amplitudeInitialized) {
    initServerSideAmplitudeAnalytics();
  }

  ampli.client.track(eventType, eventProperties, {
    device_id: deviceId,
    session_id: sessionId || undefined,
  });
};

// ------ Amplitude Experiments/Feature Flags ------
export const loadAmplitudeExperimentVariantsServer = async (user?: SharedAnalyticsUserPropertyArgs['user'] | null) => {
  // by default we don't run on our local environments
  if (process.env.NEXT_PUBLIC_USE_AMPLITUDE !== 'true' || !AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY) return;

  if (!experimentServer) {
    experimentServer = Experiment.initializeRemote(AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY, FLAG_FETCH_CONFIG);
  }

  const userProperties = user?.id ? buildAmplitudeUserProperties({ user: user as SharedAnalyticsUserPropertyArgs['user'] }) : {};

  try {
    variants = await experimentServer.fetchV2({ user_id: user?.id, user_properties: userProperties });
  } catch (error) {
    logger.warn('Amplitude experiment fetch failed, falling back to defaults', error);
    return undefined;
  }

  return variants;
};

/**
 * Server-side function for determining if a user is exposed to an experiment
 * and returns what variant the user was assigned to.
 *
 * Amplitude will automatically fire an exposure event when the variant fetch happens
 * on the client-side. Since this is on the server-side, you will need to manually
 * fire an exposure event for the experiment data to run correctly. You can use the
 * `useAmplitudeExperimentExposureEvent` hook to dispatch the exposure event on the client-side.
 */
export const getAmplitudeExperimentVariantServer = async (
  experimentKey: AmplitudeExperimentKey,
  user?: SharedAnalyticsUserPropertyArgs['user'],
) => {
  variants = await loadAmplitudeExperimentVariantsServer(user);
  const variant = variants?.[experimentKey];

  // If we didn't find that variant or the variant is 'off' (i.e. the user isn't part of the experiment targeting group), return 'control'
  if (!variant || variant.value === OFF) return CONTROL;
  return variant.value;
};

export const getAmplitudeFeatureFlagServer = async (
  featureFlagKey: AmplitudeFeatureFlagKey,
  user?: SharedAnalyticsUserPropertyArgs['user'] | null,
) => (await getKnownAmplitudeFeatureFlagServer(featureFlagKey, user)) ?? false;

/** `undefined` only when Amplitude is configured but the fetch failed; disabled-by-config reads as off, like the client. */
export const getKnownAmplitudeFeatureFlagServer = async (
  featureFlagKey: AmplitudeFeatureFlagKey,
  user?: SharedAnalyticsUserPropertyArgs['user'] | null,
): Promise<boolean | undefined> => {
  const override = LOCAL_OVERRIDES.get(featureFlagKey);
  if (override !== undefined) return override !== OFF;

  if (process.env.NEXT_PUBLIC_USE_AMPLITUDE !== 'true' || !AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY) return false;

  variants = await loadAmplitudeExperimentVariantsServer(user);
  if (variants === undefined) return undefined;
  return isAmplitudeFeatureFlagOnInVariants(featureFlagKey, variants);
};

/** Several flags from one variants fetch, each read as `getAmplitudeFeatureFlagServer` reads it alone. */
export const getAmplitudeFeatureFlagsServer = async <K extends AmplitudeFeatureFlagKey>(
  featureFlagKeys: readonly K[],
  user?: SharedAnalyticsUserPropertyArgs['user'] | null,
): Promise<Record<K, boolean>> => {
  // With Amplitude off the loader returns undefined without fetching.
  const needsFetch = featureFlagKeys.some((key) => !LOCAL_OVERRIDES.has(key));
  const fetched = needsFetch ? await loadAmplitudeExperimentVariantsServer(user) : undefined;
  // An override wins; with nothing fetched, every other flag reads off.
  return Object.fromEntries(featureFlagKeys.map((key) => [key, isAmplitudeFeatureFlagOnInVariants(key, fetched)])) as Record<K, boolean>;
};

/** Reads a flag from already-fetched variants, so callers holding them avoid a second fetch. */
export const isAmplitudeFeatureFlagOnInVariants = (
  featureFlagKey: AmplitudeFeatureFlagKey,
  fetchedVariants: Partial<Record<string, Variant>> | undefined,
) => {
  const override = LOCAL_OVERRIDES.get(featureFlagKey);
  if (override !== undefined) return override !== OFF;
  return fetchedVariants?.[featureFlagKey]?.value === ON;
};
